/**
 * Tool Items Editor
 *
 * Component for managing tool items in a template.
 * Supports adding, editing, reordering, and deleting tool items.
 */

import { useState } from 'react';
import {
  Plus,
  GripVertical,
  Trash2,
  ChevronDown,
  ChevronUp,
  Wrench,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Card } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import type { TemplateToolItem, ToolCategory } from '../types';

interface ToolItemsEditorProps {
  items: TemplateToolItem[];
  onChange: (items: TemplateToolItem[]) => void;
}

const TOOL_CATEGORIES: { value: ToolCategory; label: string }[] = [
  { value: 'electrical', label: 'Electrical' },
  { value: 'safety', label: 'Safety' },
  { value: 'general', label: 'General' },
  { value: 'measurement', label: 'Measurement' },
  { value: 'installation', label: 'Installation' },
  { value: 'other', label: 'Other' },
];

const generateKey = (name: string): string => {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .substring(0, 50);
};

export function ToolItemsEditor({ items, onChange }: ToolItemsEditorProps) {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  const [newItem, setNewItem] = useState<Partial<TemplateToolItem> | null>(null);

  const handleAddItem = () => {
    setNewItem({
      key: '',
      name: '',
      description: '',
      category: 'general',
      required: true,
      sortOrder: items.length,
    });
  };

  const handleSaveNewItem = () => {
    if (!newItem || !newItem.name?.trim()) return;

    const key = newItem.key?.trim() || generateKey(newItem.name);

    const item: TemplateToolItem = {
      key,
      name: newItem.name.trim(),
      description: newItem.description?.trim() || undefined,
      category: newItem.category || 'general',
      required: newItem.required ?? true,
      sortOrder: items.length,
    };

    onChange([...items, item]);
    setNewItem(null);
  };

  const handleCancelNewItem = () => {
    setNewItem(null);
  };

  const handleUpdateItem = (index: number, updates: Partial<TemplateToolItem>) => {
    const updated = [...items];
    updated[index] = { ...updated[index], ...updates };
    onChange(updated);
  };

  const handleDeleteItem = (index: number) => {
    const updated = items.filter((_, i) => i !== index);
    updated.forEach((item, i) => {
      item.sortOrder = i;
    });
    onChange(updated);
    if (expandedIndex === index) setExpandedIndex(null);
  };

  const handleMoveUp = (index: number) => {
    if (index === 0) return;
    const updated = [...items];
    [updated[index - 1], updated[index]] = [updated[index], updated[index - 1]];
    updated.forEach((item, i) => {
      item.sortOrder = i;
    });
    onChange(updated);
    setExpandedIndex(index - 1);
  };

  const handleMoveDown = (index: number) => {
    if (index === items.length - 1) return;
    const updated = [...items];
    [updated[index], updated[index + 1]] = [updated[index + 1], updated[index]];
    updated.forEach((item, i) => {
      item.sortOrder = i;
    });
    onChange(updated);
    setExpandedIndex(index + 1);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Define the tools technicians need to bring for this installation.
        </p>
        <Button size="sm" onClick={handleAddItem} disabled={!!newItem}>
          <Plus className="h-4 w-4 mr-2" />
          Add Tool
        </Button>
      </div>

      {/* Items List */}
      <div className="space-y-2">
        {items.map((item, index) => (
          <Collapsible
            key={`${item.key}-${index}`}
            open={expandedIndex === index}
            onOpenChange={(open) => setExpandedIndex(open ? index : null)}
          >
            <Card>
              <div className="flex items-center gap-2 p-3">
                <div className="flex flex-col gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-5 w-5"
                    onClick={() => handleMoveUp(index)}
                    disabled={index === 0}
                  >
                    <ChevronUp className="h-3 w-3" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-5 w-5"
                    onClick={() => handleMoveDown(index)}
                    disabled={index === items.length - 1}
                  >
                    <ChevronDown className="h-3 w-3" />
                  </Button>
                </div>

                <GripVertical className="h-4 w-4 text-muted-foreground" />

                <CollapsibleTrigger asChild>
                  <div className="flex-1 flex items-center gap-3 cursor-pointer">
                    <Wrench className="h-4 w-4 text-muted-foreground" />
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{item.name}</span>
                        {item.required && (
                          <Badge variant="secondary" className="text-xs">
                            Required
                          </Badge>
                        )}
                        {item.category && (
                          <Badge variant="outline" className="text-xs">
                            {TOOL_CATEGORIES.find((c) => c.value === item.category)?.label || item.category}
                          </Badge>
                        )}
                      </div>
                      <span className="text-xs text-muted-foreground font-mono">
                        {item.key}
                      </span>
                    </div>
                    <ChevronDown className="h-4 w-4 text-muted-foreground" />
                  </div>
                </CollapsibleTrigger>

                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-destructive hover:text-destructive"
                  onClick={() => handleDeleteItem(index)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>

              <CollapsibleContent>
                <div className="border-t px-3 py-4 space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Tool Name *</Label>
                      <Input
                        value={item.name}
                        onChange={(e) =>
                          handleUpdateItem(index, { name: e.target.value })
                        }
                        placeholder="e.g., Digital Multimeter"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Key</Label>
                      <Input
                        value={item.key}
                        onChange={(e) =>
                          handleUpdateItem(index, { key: e.target.value })
                        }
                        placeholder="Auto-generated from name"
                        className="font-mono text-sm"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Category</Label>
                      <Select
                        value={item.category || 'general'}
                        onValueChange={(value) =>
                          handleUpdateItem(index, { category: value as ToolCategory })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {TOOL_CATEGORIES.map((cat) => (
                            <SelectItem key={cat.value} value={cat.value}>
                              {cat.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex items-center justify-between rounded-lg border p-3">
                      <Label htmlFor={`required-${index}`}>Required</Label>
                      <Switch
                        id={`required-${index}`}
                        checked={item.required}
                        onCheckedChange={(checked) =>
                          handleUpdateItem(index, { required: checked })
                        }
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Description</Label>
                    <Textarea
                      value={item.description || ''}
                      onChange={(e) =>
                        handleUpdateItem(index, { description: e.target.value })
                      }
                      placeholder="Optional description or specifications..."
                      rows={2}
                    />
                  </div>
                </div>
              </CollapsibleContent>
            </Card>
          </Collapsible>
        ))}

        {/* New Item Form */}
        {newItem && (
          <Card className="border-primary">
            <div className="p-4 space-y-4">
              <div className="flex items-center gap-2">
                <Wrench className="h-4 w-4" />
                <span className="font-medium">New Tool</span>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Tool Name *</Label>
                  <Input
                    value={newItem.name || ''}
                    onChange={(e) =>
                      setNewItem({ ...newItem, name: e.target.value })
                    }
                    placeholder="e.g., Digital Multimeter"
                    autoFocus
                  />
                </div>
                <div className="space-y-2">
                  <Label>Key</Label>
                  <Input
                    value={newItem.key || ''}
                    onChange={(e) =>
                      setNewItem({ ...newItem, key: e.target.value })
                    }
                    placeholder="Auto-generated from name"
                    className="font-mono text-sm"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Category</Label>
                  <Select
                    value={newItem.category || 'general'}
                    onValueChange={(value) =>
                      setNewItem({ ...newItem, category: value as ToolCategory })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TOOL_CATEGORIES.map((cat) => (
                        <SelectItem key={cat.value} value={cat.value}>
                          {cat.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center justify-between rounded-lg border p-3">
                  <Label>Required</Label>
                  <Switch
                    checked={newItem.required ?? true}
                    onCheckedChange={(checked) =>
                      setNewItem({ ...newItem, required: checked })
                    }
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  value={newItem.description || ''}
                  onChange={(e) =>
                    setNewItem({ ...newItem, description: e.target.value })
                  }
                  placeholder="Optional description or specifications..."
                  rows={2}
                />
              </div>

              <div className="flex justify-end gap-2">
                <Button variant="outline" size="sm" onClick={handleCancelNewItem}>
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={handleSaveNewItem}
                  disabled={!newItem.name?.trim()}
                >
                  Add Tool
                </Button>
              </div>
            </div>
          </Card>
        )}

        {items.length === 0 && !newItem && (
          <div className="text-center py-8 text-muted-foreground">
            No tools defined. Click "Add Tool" to add tools technicians need to bring.
          </div>
        )}
      </div>
    </div>
  );
}
